// src/components/profile-detail/ProfileTabs.jsx
import React, { useState, useRef, useEffect } from "react";
import {
  Tabs,
  Tab,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  IconButton,
  Menu,
  MenuItem,
  Fade
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CakeIcon from '@mui/icons-material/Cake';
import SchoolIcon from '@mui/icons-material/School';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import VerifiedIcon from '@mui/icons-material/Verified';
import StarIcon from '@mui/icons-material/Star';
import MenuIcon from '@mui/icons-material/Menu';
import OverviewTab from "./OverviewTab";
import EducationCareerTab from "./EducationCareerTab";
import FamilyHealthTab from "./FamilyHealthTab";
import PreferencesTab from "./PreferencesTab";
import VerificationTab from "./VerificationTab";
import ZodiacTab from "./ZodiacTab";

const ProfileTabs = ({ activeTab, onTabChange, profile, genderInfo }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState(null);
  const [scrollable, setScrollable] = useState(false);
  const tabsRef = useRef(null);
  const open = Boolean(anchorEl);

  const tabPanels = [
    {
      label: 'Overview',
      icon: <CakeIcon fontSize="small" />,
      component: <OverviewTab profile={profile} genderInfo={genderInfo} />
    },
    {
      label: 'Education & Career',
      icon: <SchoolIcon fontSize="small" />,
      component: <EducationCareerTab profile={profile} genderInfo={genderInfo} />
    },
    {
      label: 'Family & Health',
      icon: <FamilyRestroomIcon fontSize="small" />,
      component: <FamilyHealthTab profile={profile} genderInfo={genderInfo} />
    },
    {
      label: 'Preferences',
      icon: <FavoriteBorderIcon fontSize="small" />,
      component: <PreferencesTab profile={profile} genderInfo={genderInfo} />
    },
    {
      label: 'Verification',
      icon: <VerifiedIcon fontSize="small" />,
      component: <VerificationTab profile={profile} genderInfo={genderInfo} />
    },
    {
      label: 'Zodiac',
      icon: <StarIcon fontSize="small" />,
      component: <ZodiacTab profile={profile} genderInfo={genderInfo} />
    }
  ];

  // Check if tabs need to be scrollable
  useEffect(() => {
    const checkScrollable = () => {
      if (tabsRef.current) {
        const tabsContainer = tabsRef.current;
        const tabElements = tabsContainer.querySelectorAll('.MuiTab-root');
        let totalWidth = 0;

        tabElements.forEach(tab => {
          totalWidth += tab.offsetWidth;
        });

        setScrollable(totalWidth > tabsContainer.offsetWidth);
      }
    };

    checkScrollable();
    window.addEventListener('resize', checkScrollable);
    return () => window.removeEventListener('resize', checkScrollable);
  }, []);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (index) => {
    onTabChange(null, index);
    handleMenuClose();
  };

  // Mobile view - Dropdown menu with icons
  if (isMobile) {
    return (
      <>
        <Box sx={{
          backgroundColor: theme.palette.background.alt,
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider'
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}>
              <Box sx={{
                color: genderInfo.color,
                display: 'flex',
                alignItems: 'center'
              }}>
                {tabPanels[activeTab]?.icon}
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: genderInfo.color }}>
                {tabPanels[activeTab]?.label}
              </Typography>
            </Box>
            <IconButton
              onClick={handleMenuClick}
              sx={{
                color: genderInfo.color,
                backgroundColor: alpha(genderInfo.color, 0.1),
                '&:hover': {
                  backgroundColor: alpha(genderInfo.color, 0.2)
                }
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          TransitionComponent={Fade}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 250,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              borderRadius: 2,
              '& .MuiMenuItem-root': {
                py: 1.5,
                px: 2,
                gap: 1.5,
                '&.Mui-selected': {
                  backgroundColor: alpha(genderInfo.color, 0.1),
                  '& .MuiListItemIcon-root': {
                    color: genderInfo.color
                  }
                }
              }
            }
          }}
        >
          {tabPanels.map((tab, index) => (
            <MenuItem
              key={index}
              selected={index === activeTab}
              onClick={() => handleMenuItemClick(index)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5
              }}
            >
              <Box sx={{
                color: index === activeTab ? genderInfo.color : 'text.secondary',
                display: 'flex',
                alignItems: 'center',
                width: 24
              }}>
                {tab.icon}
              </Box>
              <Typography
                variant="whizText2"
                sx={{
                  fontWeight: index === activeTab ? 600 : 500,
                  color: index === activeTab ? genderInfo.color : 'text.primary'
                }}
              >
                {tab.label}
              </Typography>
            </MenuItem>
          ))}
        </Menu>

        <Box sx={{ p: { xs: 2, sm: 3 }, backgroundColor: theme.palette.background.default }}>
          {tabPanels[activeTab]?.component}
        </Box>

        {/* Mobile Footer */}
        <Box sx={{
          p: { xs: 2, md: 3 },
          backgroundColor: theme.palette.background.alt,
          borderTop: 1,
          borderColor: 'divider',
          textAlign: 'center',
          borderBottomLeftRadius: 12,
          borderBottomRightRadius: 12
        }}>
          <Typography variant="whizText2" color="text.secondary" sx={{ fontWeight: 500 }}>
            Profile ID: <strong style={{ color: genderInfo.color }}>{profile?.profile_id}</strong> •
            Created on {profile?.profile_created_date || 'N/A'} by {profile?.profile_created_by || 'Admin'}
          </Typography>
        </Box>
      </>
    );
  }

  // Desktop view - Horizontal tabs
  return (
    <>
      <Box
        ref={tabsRef}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: theme.palette.background.alt,
          px: { xs: 1, md: 2 },
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Tabs
          value={activeTab}
          onChange={onTabChange}
          variant={scrollable ? "scrollable" : "standard"}
          scrollButtons={scrollable ? "auto" : false}
          allowScrollButtonsMobile={scrollable}
          sx={{
            minHeight: 60,
            '& .MuiTab-root': {
              fontSize: { xs: '0.85rem', md: '0.9rem', lg: '1rem' },
              fontWeight: 600,
              minHeight: 60,
              textTransform: 'none',
              px: { xs: 1.5, md: 2.5 },
              gap: 1,
              '&.Mui-selected': {
                color: genderInfo.color,
              },
              '&:hover': {
                color: genderInfo.color,
                backgroundColor: alpha(genderInfo.color, 0.05)
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: genderInfo.color,
              height: 3,
              borderRadius: '3px 3px 0 0'
            },
            '& .MuiTabs-scrollButtons': {
              width: 40,
              color: genderInfo.color,
              '&.Mui-disabled': {
                opacity: 0.3
              }
            }
          }}
        >
          {tabPanels.map((tab, index) => (
            <Tab
              key={index}
              label={
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  whiteSpace: 'nowrap'
                }}>
                  {tab.icon}
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 'inherit',
                      fontWeight: 'inherit'
                    }}
                  >
                    {tab.label}
                  </Typography>
                </Box>
              }
            />
          ))}
        </Tabs>
      </Box>

      <Box sx={{ p: { xs: 2, md: 4 }, backgroundColor: theme.palette.background.default }}>
        {tabPanels[activeTab]?.component}
      </Box>

      {/* Footer */}
      <Box sx={{
        p: { xs: 2, md: 3 },
        backgroundColor: theme.palette.background.alt,
        borderTop: 1,
        borderColor: 'divider',
        textAlign: 'center',
        borderBottomLeftRadius: 12,
        borderBottomRightRadius: 12
      }}>
        <Typography variant="whizText2" color="text.secondary" sx={{ fontWeight: 500 }}>
          Profile ID: <strong style={{ color: genderInfo.color }}>{profile?.profile_id}</strong> •
          Created on {profile?.profile_created_date || 'N/A'} by {profile?.profile_created_by || 'Admin'}
        </Typography>
      </Box>
    </>
  );
};

export default ProfileTabs;